import { Injector } from "@angular/core";
import { WorkplaceActionHandlerBase } from "../grid-base/actions/workplace-action-handler-base";
import { DestroyBase } from "../ng-destroy-base";
import { TabConfig } from "../pollux-tab-config/pollux-form-config";
import { WorkplaceAreaHostBase } from "../workplace-area-host-base/workplace-area-host-base";
import { WorkspaceBase } from "../workspace-base/workspace-base";
import { EventBusService } from "../../../services/event-bus.service";

/**
 * Workplace base class. A workplace coordinates multiple workspaces
 */
export abstract class WorkplaceBase extends DestroyBase {
  /**
   * The EventBusService
   */
  protected eventBus: EventBusService;

  /**
   * Sets the host component
   */
  public abstract set main(value: WorkplaceAreaHostBase);

  /**
   * Gets the host component
   */
  public abstract get main(): WorkplaceAreaHostBase;

  /**
   * The search mode
   */
  public searchMode: string;

  /**
   * The tabs / areas
   */
  public tabs: WorkspaceBase[] = [];

  /**
   * The area selected for importing
   */
  public selectedImportArea: string = null;

  /**
   * The previously selected tab item
   */
  public previouslySelectedTabItem: any = null;

  /**
   * The previously selected item
   */
  public previouslySelectedItem: any = null;

  /**
   * The selected tab hierarchy
   */
  public selectedTabHierarchy: number = null;

  /**
   * The selected item in the current tab hierarchy
   */
  public selectedTabHierarchyItem: any = null;

  /**
   * The selected tab hierarchy type
   */
  public selectedTabHierarchyItemType: string = null;

  /**
   * The selected tab / area (ID)
   */
  public selectedTab = "#tabProjectstd"

  /**
   * Gets the action handlers used by this workspace area. Implement all logic in these handlers, NOT in the derived class of this class.
   */
  protected abstract get actionHandlers(): WorkplaceActionHandlerBase[];

  /**
   * Gets the tab config
   */
  public abstract get tabConfig(): TabConfig[];

  /**
   * Constructor
   * @param injector
   */
  constructor(injector: Injector) {
    super();
    this.eventBus = injector.get(EventBusService);
  }

  /**
   * Initializes the workplace
   */
  protected init(): void {
    this.searchMode = this.tabConfig[0].searchMode;
    this.main.onWorkplaceCreated(this);
    this.registerActionHandlers(this.actionHandlers);
    this.registerEvents();
  }

  /**
   * Registers an array of GridActionHandlerBase instances. These keep the workplace logic
   * @param handlers The handlers
   */
  private registerActionHandlers(handlers: WorkplaceActionHandlerBase[]): void {
    if (handlers?.length) {
      for (const handler of handlers) {
        this.registerActionHandler(handler);
      }
    }
  }

  /**
  * Registers a single GridActionHandlerBase instance. These keep the workplace logic
  * @param handlers The handlers
  */
  private registerActionHandler(handler: WorkplaceActionHandlerBase): void {
    if (handler?.topic) {
      this.onEvents.push(this.eventBus.subscribeEvent(handler.topic, (payload: any, _options) => {
        handler.handle(payload);
      }));
    }
    else {
      console.error('Invalid setup: handler or it\'s topic  is null or undefined!');
    }
  }

  /**
   * Registers events
   */
  protected abstract registerEvents(): void;

  /**
   * Track by id helper
   * @param _index Index
   * @param tab Tab
   * @returns Thetab's ID for *ngFor
   */
  public trackById_(index: number, tab: any): any {
    return tab.id;
  }

  /**
   * Sets the counter value
   * @param area Area
   * @param amount Amount
   */
  public setCounterValue(area: string, amount: number): void {
    this.main.setCounterValue(area, amount);
  }

  /**
   * Called if a tab/workspace was created
   * @param tab The tab
   */
  public onTabCreated(tab: WorkspaceBase): void {
    this.tabs.push(tab);

    if (tab.tabElement.tabIndex == 0) {
      //init selectedImportArea for first tab, which is active by default
      this.main.setSelectedImportArea(tab.tabElement.exportAreaName || '');
    }
  }

  /**
   * Called if an item was selected in any tab of the current area.
   * @param tabItem The tab item
   * @param item The item
   * @param _skipSelf Not used
   */
  public onElementSelected(tabItem: TabConfig, item: any, _skipSelf: boolean): void {
    if (!tabItem.isLeafHierarchy) {
      //This tab has filtered tabs

      try {
        if (this.previouslySelectedTabItem && this.previouslySelectedItem) {
          if (this.previouslySelectedTabItem == JSON.stringify(tabItem) && this.previouslySelectedItem == JSON.stringify(item)) {
            return;
          }
        }

        this.previouslySelectedTabItem = JSON.stringify(tabItem);
        this.previouslySelectedItem = JSON.stringify(item);
      } catch (e) {
        //Ignore
      }

      this.selectedTabHierarchy = tabItem.tabHierarchy;
      this.selectedTabHierarchyItem = item;
      this.selectedTabHierarchyItemType = tabItem.searchMode;

      this.filterInTabs();
    }

    this.main.onElementSelected(tabItem, item);
  }

  /**
   * Apply the filtering in all tabs
   */
  private filterInTabs(): void {
    for (const tab of this.tabs) {
      if (this.selectedTabHierarchyItem.length > 0) {
        tab.filter(this.selectedTabHierarchyItem[0], this.selectedTabHierarchy, this.tabConfig, false);
      }
      else {
        tab.filter(this.selectedTabHierarchyItem, this.selectedTabHierarchy, this.tabConfig, false);
      }
    }
  }

  /**
   * Called if an item was deselected
   * @param tabItem The tab where it was deselected
   */
  public onElementDeselected(tabItem: any): void {
    if (tabItem.isLeafHierarchy) {
      //This tab doesn't have any filtered tabs
      return;
    }
    if (this.selectedTabHierarchy == tabItem.tabHierarchy) {
      this.previouslySelectedTabItem = null;
      this.previouslySelectedItem = null;
      this.selectedTabHierarchy = null;
      this.selectedTabHierarchyItem = null;
      this.selectedTabHierarchyItemType = null;

      for (const tab of this.tabs) {
        tab.filter(this.selectedTabHierarchyItem, this.selectedTabHierarchy, this.tabConfig, false);
      }

      this.main.onElementDeselected(tabItem);
    }
  }

  /**
   * Called if show all was triggered
   * @param _tab: Tab
   */
  public onShowAll(_tab: TabConfig): void {
    this.selectedTabHierarchy = null;
    this.selectedTabHierarchyItem = null;
    this.selectedTabHierarchyItemType = null;

    for (const tab of this.tabs) {
      tab.filter(this.selectedTabHierarchyItem, this.selectedTabHierarchy, this.tabConfig, true);
    }
  }

  /**
   * Triggers an export - if supported
   */
  public doExport(): void {
    for (const tab of this.tabs) {
      if (this.main.selectedImportArea === tab.tabElement.exportAreaName) {
        tab.doExport(tab.tabElement.exportAreaName);
      }
    }
  }

  /**
   * Called if a search was triggered
   * @param tab: Tab
   */
  public onSearchClicked(tab: TabConfig): void {
    this.onShowAll(tab);
  }
}
